import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';

import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { Icon } from '../components/icons';
import { PageLoader } from '../components/Loader';
import { mapsDirectionsUrl } from '../places/maps';
import { addDays, addressLine, appointmentStatusLabels, fmtDate, fmtTime, localDate, travelLabel } from '../ops/format';
import { RoutePanel } from '../ops/RoutePanel';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

// FieldSalesPage: Außendienst-Tagesansicht mit Fokus auf HEUTE – ohne Akquise-Funktionen.
export function FieldSalesPage({ embedded = false }: { embedded?: boolean }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? localDate();
  const day = useQuery({
    queryKey: projectKeys.day(project.id, date),
    queryFn: ({ signal }) => papi.fieldSalesDay(date, undefined, signal),
    refetchInterval: 120_000,
  });
  const route = useQuery({
    queryKey: projectKeys.route(project.id, date),
    queryFn: ({ signal }) => papi.fieldSalesRoute(date, undefined, signal),
    enabled: (day.data?.items.length ?? 0) > 0,
  });
  const travel = new Map((route.data?.stops ?? []).map((s) => [s.appointment_id, s.travel_minutes]));
  const isToday = date === localDate();
  // Tag wechseln, ohne andere Parameter (z. B. den Reiter unter „Termine“) zu verlieren.
  const go = (d?: string) => {
    const next = new URLSearchParams(params);
    if (d) next.set('date', d);
    else next.delete('date');
    setParams(next);
  };
  const Title = embedded ? 'h2' : 'h1';

  return (
    <div className={embedded ? 'embedded-page field-sales' : 'page field-sales'}>
      <header className="page-header">
        <Title>{isToday ? 'Heute' : fmtDate(new Date(`${date}T12:00:00Z`))}</Title>
        <div className="calendar-nav">
          <button type="button" className="btn btn-small" onClick={() => go(addDays(date, -1))} aria-label="Vorheriger Tag">
            ‹
          </button>
          {!isToday && (
            <button type="button" className="btn btn-small" onClick={() => go()}>
              Heute
            </button>
          )}
          <button type="button" className="btn btn-small" onClick={() => go(addDays(date, 1))} aria-label="Nächster Tag">
            ›
          </button>
        </div>
      </header>
      {day.data && (
        <p className="day-summary">
          <strong>{day.data.summary.appointments}</strong> Termine
          {day.data.summary.changed > 0 && <> · <strong>{day.data.summary.changed}</strong> geändert</>}
          {day.data.summary.hints > 0 && <> · <strong>{day.data.summary.hints}</strong> neue Hinweise</>}
        </p>
      )}
      {day.isPending ? (
        <PageLoader />
      ) : day.isError ? (
        <ErrorMessage error={day.error} onRetry={() => void day.refetch()} />
      ) : day.data.items.length === 0 ? (
        <EmptyState title="Keine Termine" icon="calendar">
          Für diesen Tag sind keine Termine eingeplant – mit ‹ und › wechseln Sie den Tag.
        </EmptyState>
      ) : (
        <ol className="agenda">
          {day.data.items.map((it) => (
            <li key={it.id} className={it.status === 'CANCELED' ? 'agenda-item canceled' : 'agenda-item'}>
              <Link to={`/p/${project.key}/termine/${it.id}`}>
                <span className="agenda-time">{fmtTime(it.start_at)}</span>
                <span className="agenda-body">
                  <strong>{it.lead_name ?? 'Termin'}</strong>
                  <span className="muted">{it.city ?? addressLine(it)}</span>
                  {travel.get(it.id) != null && <span className="muted">Fahrzeit: {travelLabel(travel.get(it.id))}</span>}
                  {it.brief_short && <span className="agenda-brief">{it.brief_short}</span>}
                </span>
                <span className="badges">
                  {it.type_name && <span className="badge badge-muted">{it.type_name}</span>}
                  {it.status !== 'PLANNED' && <span className="badge">{appointmentStatusLabels[it.status]}</span>}
                  {it.changed && <span className="badge badge-warn">geändert</span>}
                  {it.address_differs_from_lead && <span className="badge badge-warn">andere Adresse</span>}
                  {it.handback_kind && (
                    <span className={it.handback_priority === 'URGENT' ? 'badge badge-error' : 'badge badge-warn'}>
                      {it.handback_priority === 'URGENT' ? 'beim Support (dringend)' : 'beim Support'}
                    </span>
                  )}
                </span>
              </Link>
              {it.status !== 'CANCELED' && addressLine(it) !== '–' && (
                <div className="agenda-actions">
                  <a className="btn btn-small btn-navigate" href={mapsDirectionsUrl(it.street, it.house_number, it.postal_code, it.city)} target="_blank" rel="noreferrer">
                    <Icon name="navigate" size={16} /> Navigation starten
                  </a>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {route.data && route.data.stops.length > 0 && <RoutePanel route={route.data} />}
    </div>
  );
}
